import { compact, uniq } from 'lodash';
import { presence } from '../util/values';
import { apollo } from './graphql';
import { Product } from './product';
import { GET_STAT_PRODUCT_VARIANTS } from './queries/merchStatistics';
import { ProductStatus } from '../generated/graphql';

export interface MerchStatsVariant {
  product?: Partial<Product>;
  id?: string;
  description?: string | null;
  ean?: string | null;
  sku?: string;
  statistics?: {
    soldUnfulfilled: number;
    availableStock: number;
    clearingStock: number;
    closingStock: number;
    incomingStock: number;
    initialStock: number;
    lostStock: number;
    outgoingStock: number;
    refund: number;
    refundDelivered: number;
    reserved: number;
    sentIncomplete: number;
    sold: number;
    soldDelivered: number;
    netSold: number;
  };
}

interface QueryMerchStatsProps {
  terms?: string;
  startDate?: string;
  endDate?: string;
  productStatuses?: string;
  categories?: string;
  onlyActive?: string;
  skuList?: string;
}

export interface MerchStatsProps {
  after: string | null;
  terms: string[] | null;
  startDate: ISO8601DateTime | null;
  endDate: ISO8601DateTime | null;
  productStatuses: ProductStatus[] | null;
  productCategoryIds: string[] | null;
  onlyActive: boolean | null;
  skuList: string[] | null;
}

export function parseStatsParams(props: QueryMerchStatsProps): MerchStatsProps {
  const parsedTerms = props.terms?.split('&').filter(Boolean);
  const parsedproductStatuses = props.productStatuses?.split('*').filter(Boolean);
  const parsedProductCategoryIds = props.categories?.split('*').filter(Boolean);
  const skuList = props.skuList?.split('*').filter(Boolean);

  return {
    terms: presence(parsedTerms) ?? null,
    productStatuses: presence(parsedproductStatuses)?.map((e) => e as ProductStatus) ?? null,
    productCategoryIds: presence(parsedProductCategoryIds) ?? null,
    startDate: props.startDate ? new Date(Number(props.startDate)).toISOString() : null,
    endDate: props.endDate ? new Date(Number(props.endDate)).toISOString() : null,
    onlyActive: props.onlyActive === 'true' ? true : false,
    skuList: presence(skuList) ?? null,
    after: null,
  };
}

export async function getMerchStatisticsVariants(variables: MerchStatsProps): Promise<{
  variants: MerchStatsVariant[];
  hasNextPage: boolean;
  endCursor: string | null;
}> {
  const result = await apollo.query({
    query: GET_STAT_PRODUCT_VARIANTS,
    variables: {
      first: 10,
      ...variables,
    },
    fetchPolicy: 'network-only',
  });

  const variants: MerchStatsVariant[] = compact(
    result.data.productVariants.edges?.map((item) => {
      return { ...item?.node };
    })
  );

  const { hasNextPage, endCursor } = result.data.productVariants.pageInfo;

  return {
    variants,
    hasNextPage,
    endCursor: endCursor ?? null,
  };
}

export function setBackgroundByProductId(data?: MerchStatsVariant[]): Array<MerchStatsVariant & { className: string }> {
  if (!data?.length) return [];

  const productIds = uniq(data.map((item) => item.product?.slug));

  return data.map((item) => {
    return {
      ...item,
      className: productIds.indexOf(item.product?.slug) % 2 === 0 ? 'background-white' : 'background-gray',
    };
  });
}
